import { useState } from "react";
import { Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export default function AusenciaParcialWorkerPanel({ solicitud, onActualizar }) {
  const pendientes = solicitud.ausencia_pendiente_trabajador || [];
  const [sel, setSel] = useState(() => new Set(pendientes.map((u) => u.id)));
  const [motivo, setMotivo] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);

  if (!pendientes.length) return null;

  const toggle = (id) => {
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const subirAdjunto = async () => {
    if (!archivo) return null;
    const fd = new FormData();
    fd.append("archivo", archivo);
    const { data } = await api.post(`/solicitudes/${solicitud.id}/ausencia-parcial/adjunto`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.id;
  };

  const responder = async (accion) => {
    const ids = [...sel];
    if (!ids.length) {
      toast.error("Selecciona al menos un tramo");
      return;
    }
    if (accion === "justificar" && motivo.trim().length < 3 && !archivo) {
      toast.error("Indica un motivo o adjunta un documento");
      return;
    }
    setEnviando(true);
    try {
      let adjunto_id = null;
      if (accion === "justificar" && archivo) {
        adjunto_id = await subirAdjunto();
      }
      await api.post(`/solicitudes/${solicitud.id}/ausencia-parcial/responder`, {
        accion,
        unidad_ids: ids,
        motivo: motivo.trim(),
        adjunto_id,
      });
      toast.success(accion === "cancelar" ? "Tramos cancelados" : "Justificación enviada al encargado");
      setMotivo("");
      setArchivo(null);
      onActualizar?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="ausencia-parcial-worker">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
        Tramos no aprobados — tu respuesta
      </p>
      <ul className="mt-2 space-y-2">
        {pendientes.map((u) => (
          <li key={u.id} className="flex items-start gap-2 text-sm text-zinc-700">
            <Checkbox checked={sel.has(u.id)} onCheckedChange={() => toggle(u.id)} />
            <span>
              {u.label}
              {u.comentario_revision && (
                <span className="mt-0.5 block text-xs text-zinc-500">Motivo rechazo: {u.comentario_revision}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      <Textarea
        className="mt-3 min-h-[60px] bg-white text-sm"
        placeholder="Motivo o explicación (opcional si adjuntas documento)"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
      />
      <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-indigo-700">
        <Paperclip className="h-4 w-4" />
        <span>{archivo ? archivo.name : "Adjuntar documento (PDF o imagen)"}</span>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
          className="hidden"
          onChange={(e) => setArchivo(e.target.files?.[0] || null)}
        />
        {archivo && (
          <button type="button" className="text-zinc-400 hover:text-zinc-600" onClick={() => setArchivo(null)}>
            <X className="h-4 w-4" />
          </button>
        )}
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={enviando}
          onClick={() => responder("cancelar")}
          data-testid="ausencia-parcial-cancelar"
        >
          Cancelar seleccionados
        </Button>
        <Button
          size="sm"
          className="bg-indigo-600 hover:bg-indigo-700"
          disabled={enviando}
          onClick={() => responder("justificar")}
          data-testid="ausencia-parcial-justificar"
        >
          Enviar justificación
        </Button>
      </div>
    </div>
  );
}
